<template>
  <div class="reservation-service-box-wrap">
    <div class="reservation-service-box">
      <div class="initial-section">
        <div class="image" :style="getImage()" />

        <div class="content">
          <h3>{{ name }}</h3>

          <p v-html="description" />

          <div class="price">
            <span class="price-number">{{ formattedPrice(price, billedPerDay) }}</span>

            <span class="price-unit">/ cestujúci</span>
          </div>

          <div class="ordering-controls">
            <div class="order-specific">
              <button
                v-show="!moreSectionVisible && localTravellersRef.length > 1"
                type="button"
                class="btn-link text-primary"
                @click="showMoreSection"
              >
                Objednať niektorým
              </button>
            </div>

            <div v-if="!moreSectionVisible" class="order-all">
              <button
                v-if="!allTravellersHaveSelectedService"
                type="button"
                class="btn-primary-border"
                @click="selectForAllTravellers"
              >
                Objednať všetkým
              </button>

              <button
                v-else
                type="button"
                class="btn-primary-border all-selected"
                @click="deSelectForAllTravellers"
              >
                <BartoIcon icon="check-thin" />

                Objednané
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="more-section" :class="{ 'state-hidden': !moreSectionVisible }">
        <div class="row">
          <div
            v-for="(traveller, index) in localTravellersRef"
            :key="`service-${internalName}-traveller-${index}-checkbox`"
            class="col-6 m-b-sm"
          >
            <Checkbox
              :model-value="!!localTravellersRef[index].services?.[internalName]"
              @update:model-value="toggleForSingleTraveller(index, $event)"
            >
              {{ traveller.first_name }} {{ traveller.last_name }}
            </Checkbox>
          </div>
        </div>
      </div>
    </div>

    <div v-if="someTravellerHasSelectedService" class="m-t-xs italic text-right text-grey-light">
      * Cena sa prepočíta pri pokračovaní na ďaľší krok.
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import Checkbox from './input/checkbox.vue';
import { useLocalTravellers } from '../modules/useLocalTravellers';
import { usePrice } from '../modules/usePrice';
import type { Service, Traveller } from '../types';
import BartoIcon from './BartoIcon.vue';

interface ServiceProps {
  service: Service;
}

const props = defineProps<ServiceProps>();

// eslint-disable-next-line vue/no-setup-props-destructure
const { price, description, name } = props.service;

// eslint-disable-next-line vue/no-setup-props-destructure
const billedPerDay = props.service.is_billed_per_day;
// eslint-disable-next-line vue/no-setup-props-destructure
const internalName = props.service.internal_name;
// eslint-disable-next-line vue/no-setup-props-destructure
const image = props.service.reservation_image;

const {
  localTravellersRef,
  setLocalTravellers,
  selectServiceForLocalTraveller,
  deselectServiceForLocalTraveller,
} = useLocalTravellers();
const moreSectionVisible = ref(false);

const { formattedPrice } = usePrice();

const allTravellersHaveSelectedService = computed(() =>
  localTravellersRef.value.every((traveller) => !!traveller.services?.[internalName])
);

const someTravellerHasSelectedService = computed(() =>
  localTravellersRef.value.some((traveller) => !!traveller.services?.[internalName])
);

const showMoreSection = () => {
  moreSectionVisible.value = true;
};

const getImage = () => `background-image: url(${image});`;

const selectForSingleTraveller = (index: number) => {
  selectServiceForLocalTraveller(index, props.service);
};

const deSelectForSingleTraveller = (index: number) => {
  deselectServiceForLocalTraveller(index, internalName);
};

const toggleForSingleTraveller = (index: number, value: boolean) => {
  if (localTravellersRef.value[index].services[internalName]) {
    deSelectForSingleTraveller(index);
    return;
  }

  selectForSingleTraveller(index);
};

const selectForAllTravellers = () => {
  setLocalTravellers(
    localTravellersRef.value.map((item) => ({
      ...item,
      services: item.services[internalName]
        ? item.services
        : ({ ...item.services, [internalName]: props.service } as Traveller['services']),
    }))
  );
};

const deSelectForAllTravellers = () => {
  setLocalTravellers(
    localTravellersRef.value.map((item) => {
      const services = { ...item.services };
      delete services[internalName];

      return {
        ...item,
        services: {},
      };
    })
  );
};
</script>
